<template>
  <div class="stripe-subscription display">
    <div class="md-layout md-gutter">
      <div class="md-layout-item md-small-size-100">
        <p class="title md-headline">Stripe subscription</p>
      </div>
    </div>
    <div v-if="enterprise.stripeSubscription.id">
      <div class="md-layout md-gutter">
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Subscription status</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.status }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Subscription start</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.subscriptionStartedAt | dateFormat }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Subscription end at</p>
          <span class="md-subheading" v-if="enterprise.stripeSubscription.endedAt">
            {{ enterprise.stripeSubscription.endedAt | subscriptionDate }}
          </span>
          <span class="md-subheading" v-if="!enterprise.stripeSubscription.endedAt">-</span>
        </div>
      </div>

      <div class="md-layout md-gutter">
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Stripe period start</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.currentPeriodStart | dateFormat }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Stripe period end</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.currentPeriodEnd | dateFormat }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Subscription type</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.billing | subscriptionType(subscriptionTypes) }}
          </span>
        </div>
      </div>

      <div class="md-layout md-gutter">
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Billing usage type</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.plan.usageType || 'N/A' }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Aggregate usage type</p>
          <span
            class="md-subheading"
            v-if="!enterprise.stripeSubscription.plan.wrongAggregate()"
          >
            {{ enterprise.stripeSubscription.plan.aggregateUsage || 'N/A' }}
          </span>
          <div
            class="md-subheading warning"
            v-if="enterprise.stripeSubscription.plan.wrongAggregate()"
          >
            {{ enterprise.stripeSubscription.plan.aggregateUsage || 'N/A' }}
            <md-icon class="md-size-xs">info</md-icon>
            <md-tooltip>{{ $t('tooltips.billing.wrongAggregate') }}</md-tooltip>
          </div>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Units reported</p>
          <span class="md-subheading">
            <span v-for="usage in enterprise.stripeSubscriptionUsage" :key="usage.id">
              <product-text :product="usage.product" />: {{ usage.totalUsage }}
            </span>
          </span>
        </div>
      </div>

      <div class="md-layout md-gutter">
        <div class="md-layout-item md-small-size-100">
          <p class="title md-headline">Stripe plan</p>
        </div>
      </div>

      <div class="md-layout md-gutter" v-if="enterprise.stripeSubscription.plan">
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Amount</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.plan.amount | toCurrency }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Interval type and count</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.plan.intervalCount }}
            {{ enterprise.stripeSubscription.plan.interval }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Billing scheme</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.plan.billingScheme | billingScheme(billingSchemes) }}
          </span>
        </div>
      </div>

      <div class="md-layout md-gutter" v-if="enterprise.stripeSubscription.plan">
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Usage type</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.plan.usageType | usageType(usageTypes) }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Seats</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.plan.metadata.seats }}
          </span>
        </div>
        <div class="md-layout-item md-small-size-100 mb-1">
          <p class="md-subheading">Quota (allocated)</p>
          <span class="md-subheading">
            {{ enterprise.stripeSubscription.plan.metadata.quota }}
          </span>
        </div>
      </div>

      <div
        class="md-layout md-gutter"
        v-if="enterprise.stripeSubscription.plan.billingScheme === 'tiered'"
      >
        <div class="md-layout-item md-small-size-100">
          <p class="title md-headline">Stripe plan tiers</p>
        </div>
      </div>

      <div v-if="enterprise.stripeSubscription.plan.billingScheme === 'tiered'">
        <div
          class="md-layout md-gutter"
          v-for="(tier, index) in enterprise.stripeSubscription.plan.tiers" :key="index"
        >
          <div class="md-layout-item md-small-size-100 mb-1">
            <p class="md-subheading">Fixed price</p>
            <span class="md-subheading">
              {{ tier.flatAmount | toCurrency }}
            </span>
          </div>
          <div class="md-layout-item md-small-size-100 mb-1">
            <p class="md-subheading">Price per unit</p>
            <span class="md-subheading">
              {{ tier.unitAmount | toCurrency }}
            </span>
          </div>
          <div class="md-layout-item md-small-size-100 mb-1">
            <p class="md-subheading">Up to (items)</p>
            <span class="md-subheading">
              <div v-if="typeof tier.upTo === 'number'">
                {{ tier.upTo }}
              </div>
              <div v-if="typeof tier.upTo !== 'number'">
                Unlimited
              </div>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!enterprise.stripeSubscription.id && enterprise.owner.subscriptionId">
      <p class="md-subheading">
        Stripe subscription {{ enterprise.owner.subscriptionId }} could not be fetched.
      </p>
    </div>
    <div v-if="!enterprise.owner.subscriptionId">
      <p class="md-subheading">
        Stripe subscription not configured for this business.
      </p>
    </div>
  </div>
</template>
<style lang="scss">
.md-subheading.warning {
  background-color: red;
  color: white;
  padding: 0 1em;
  font-weight: bold;
}
</style>
<script type="ts">
import camelcase from 'camelcase';
import moment from 'moment';
import ProductText from '@/components/billing/product-text.vue';

export default {
  name: 'stripe-subscription-display',
  props: ['enterprise'],
  components: {
    ProductText,
  },
  filters: {
    subscriptionType(input, subscriptionTypes) {
      if (!input) return '';

      return subscriptionTypes[camelcase(input)];
    },
    billingScheme(input, billingSchemes) {
      if (!input) return '';

      return billingSchemes[camelcase(input)];
    },
    subscriptionDate(input) {
      if (!input) return '';

      return moment.unix(input).format('DD/MM/YYYY HH:mm:ss');
    },
    dateFormat(input) {
      if (!input) return '';

      return moment(input).format('DD/MM/YYYY HH:mm:ss');
    },
    usageType(input, usageTypes) {
      if (!input) return '';

      return usageTypes[camelcase(input)];
    },
  },
  data: () => ({
    billingSchemes: {
      perUnit: 'Per unit',
      tiered: 'Tiered',
    },
    subscriptionTypes: {
      sendInvoice: 'Invoice',
      chargeAutomatically: 'Automatic charge',
    },
    usageTypes: {
      metered: 'Metered',
      licensed: 'Licensed (standard)',
    },
  }),
};
</script>
